import React from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, Stack, Typography } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { commonMessages } from 'utils/commonMessages';
import { ServiceCall } from 'modules/service-call/types';
import { ImageWithFallback } from 'components/ImageWithFallback';
import { useRecoilValue } from 'recoil';
import { tenantInfoState } from 'modules/application/atoms';
import { Link } from 'react-router-dom';
import { displayProductModelAndSku } from 'utils/product';

type ServiceCallsResultDialogProps = {
  isOpen: boolean,
  onClose: () => void,
  serviceCalls: ServiceCall[]
}

export const ServiceCallsResultDialog: React.FC<ServiceCallsResultDialogProps> = (props: ServiceCallsResultDialogProps) => {
  const { formatMessage } = useIntl();
  const tenantInfo = useRecoilValue(tenantInfoState);

  return (
    <Dialog
      open={props.isOpen}
      onClose={props.onClose}
    >
      <DialogTitle>{formatMessage({ id: 'ServiceCallsResultDialog.Title', defaultMessage: 'Search result' })}</DialogTitle>
      {props.serviceCalls.length > 0 ?
        (
          <DialogContent>
            <Stack gap={2}>
              <DialogContentText>
                <FormattedMessage
                  id='ServiceCallsResultDialog.Description'
                  defaultMessage='Here is the list of service calls associated with contract number <ContractNumber></ContractNumber> and client number <ClientNumber></ClientNumber>.'
                  values={{
                    ContractNumber: () => <Typography color='primary.main' fontWeight={500} component='span'>{props.serviceCalls[0].contractNumber}</Typography>,
                    ClientNumber: () => <Typography color='primary.main' fontWeight={500} component='span'>{props.serviceCalls[0].clientNumber}</Typography>,
                  }}
                />
              </DialogContentText>
              <DialogContentText>
                <Stack gap={3}>
                  {props.serviceCalls.map(serviceCall => (
                    <Stack key={serviceCall.id} gap={1}>
                      <Divider light />
                      <Typography>{serviceCall.reason.title}</Typography>
                      <Stack direction='row' gap={2} >
                        <ImageWithFallback
                          height={100}
                          width={100}
                          src={`/${tenantInfo.identifier}/api/products/${serviceCall.product.id}/image.png`} />
                        <Stack flexGrow={1}>
                          <Stack flexGrow={1}>
                            <Typography fontWeight={500}>
                              {serviceCall.product.name}
                            </Typography>
                            <Typography variant='caption' color='text.secondary' fontWeight={500}>
                              {displayProductModelAndSku(serviceCall.product)}
                            </Typography>
                          </Stack>
                          <Stack alignItems='flex-end' direction='column-reverse'>
                            <Button variant='contained' component={Link} to={`/service-calls/${serviceCall.id}`}>
                              {formatMessage({ id: 'ServiceCallsResultDialog.Show', defaultMessage: 'Show' })}
                            </Button>
                          </Stack>
                        </Stack>
                      </Stack>
                    </Stack>
                  ))}
                  <Divider light />
                </Stack>
              </DialogContentText>
            </Stack>
          </DialogContent>
        ) : (
          <DialogContent>
            <DialogContentText color='text.primary'>{formatMessage({ id: 'ServiceCallsResultDialog.NoResult', defaultMessage: 'We couldn\'t find any service calls with the information you entered due to one of the following reasons.' })}</DialogContentText>
            <DialogContentText>
              <ul>
                <li>{formatMessage({ id: 'WhyNoServiceCallDialog.Reason1', defaultMessage: 'The number you entered is incorrect. Please review it and try again.' })}</li>
                <li>{formatMessage({ id: 'WhyNoServiceCallDialog.Reason2', defaultMessage: 'The service call as not yet been processed. Please wait atleast 24 hours.' })}</li>
                <li>{formatMessage({ id: 'WhyNoServiceCallDialog.Reason3', defaultMessage: 'The service call has been created prior and is too old' })}</li>
              </ul>
            </DialogContentText>
          </DialogContent>
        )}
      <DialogActions>
        <Button onClick={props.onClose} autoFocus>{formatMessage(commonMessages.close)}</Button>
      </DialogActions>
    </Dialog>
  );
};